import React, { useState } from "react";
import {
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  Typography,
  Box,
  Slider,
  Card,
  CardContent,
  Button,
  IconButton,
  Tooltip,
  Chip,
  CircularProgress,
  Alert,
  Paper,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import {
  AutoAwesome as AutoAwesomeIcon,
  LocationOn as LocationIcon,
  UploadFile as UploadFileIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
} from "@mui/icons-material";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import * as pdfjsLib from "pdfjs-dist";
import CourseSkillsChips from "./CourseSkillsChips";
import LocationSelector from "./LocationSelector";

// Set up PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js`;

const CreateBatchForm = ({
  formData,
  setFormData,
  showExtractedData,
  extractedData,
  modifiedBooleanString,
  setModifiedBooleanString,
  modifiedMessageTemplate,
  setModifiedMessageTemplate,
  handleExtractKeywords,
  extractingKeywords,
  setAiPromptModalOpen,
}) => {
  const [locationSelectorOpen, setLocationSelectorOpen] = useState(false);
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const [pdfError, setPdfError] = useState("");
  const [uploadedFileName, setUploadedFileName] = useState("");
  const [viewMode, setViewMode] = useState("edit"); // 'edit' or 'preview'

  // Function to convert text to markdown format
  const convertToMarkdown = (text) => {
    let markdown = text;

    // Detect and format headings based on font size and position
    const lines = markdown.split("\n");
    const formattedLines = lines.map((line, index) => {
      const trimmedLine = line.trim();
      
      // Skip empty lines
      if (!trimmedLine) return line;

      // Check if line looks like a heading (all caps, short, at start of section)
      if (
        trimmedLine.length > 3 &&
        trimmedLine.length < 100 &&
        trimmedLine === trimmedLine.toUpperCase() &&
        /^[A-Z\s\-:]+$/.test(trimmedLine)
      ) {
        return `## ${trimmedLine}\n`;
      }

      // Check for bullet points or list items
      if (/^[•●○▪▫■□-]\s/.test(trimmedLine)) {
        return `- ${trimmedLine.substring(2)}`;
      }

      // Check for numbered lists
      if (/^\d+[\.)]\s/.test(trimmedLine)) {
        return trimmedLine;
      }

      return line;
    });

    markdown = formattedLines.join("\n");

    // Clean up multiple consecutive newlines
    markdown = markdown.replace(/\n{3,}/g, "\n\n");

    return markdown;
  };

  // Function to handle PDF upload and parsing
  const handlePdfUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (file.type !== "application/pdf") {
      setPdfError("Please upload a valid PDF file");
      return;
    }

    setUploadingPdf(true);
    setPdfError("");
    setUploadedFileName(file.name);

    try {
      const arrayBuffer = await file.arrayBuffer();
      const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer });
      const pdf = await loadingTask.promise;
      let extractedText = "";

      // Extract text from all pages
      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const textContent = await page.getTextContent();
        
        // Get text items with their positions
        const textItems = textContent.items;
        let pageText = "";
        let lastY = 0;

        textItems.forEach((item, idx) => {
          const text = item.str;
          const y = item.transform[5];

          // Add line break if Y position changed significantly (new line)
          if (idx > 0 && Math.abs(y - lastY) > 5) {
            pageText += "\n";
          }

          pageText += text + " ";
          lastY = y;
        });

        extractedText += pageText.trim() + "\n\n";
      }

      // Convert extracted text to markdown format
      const markdownText = convertToMarkdown(extractedText);

      // Update form data with markdown formatted text
      if (markdownText.trim()) {
        setFormData({
          ...formData,
          job_description: markdownText.trim(),
        });
        setPdfError("");
        setViewMode("preview"); // Switch to markdown preview after upload
      } else {
        setPdfError("No text found in PDF");
      }
    } catch (error) {
      console.error("Error parsing PDF:", error);
      setPdfError("Failed to parse PDF. Please try again or enter text manually.");
    } finally {
      setUploadingPdf(false);
      // Reset file input
      event.target.value = "";
    }
  };

  // Convert location IDs to names for display
  const getLocationNames = () => {
    if (!formData.search_location || formData.search_location.length === 0) {
      return [];
    }

    // This would need to be implemented based on your location data structure
    // For now, return the IDs as they are
    return formData.search_location;
  };

  return (
    <Grid container spacing={4}>
      {/* Left Column */}
      <Grid item xs={12} md={6}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Batch Name"
              value={formData.manual_batch_name}
              onChange={(e) => {
                setFormData({
                  ...formData,
                  manual_batch_name: e.target.value,
                });
              }}
              required
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  "&:hover": {
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#1565c0",
                    },
                  },
                },
                "& .MuiInputLabel-root": {
                  fontWeight: 500,
                },
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <Box>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                  Job Description *
                </Typography>
                <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                  <ToggleButtonGroup
                    value={viewMode}
                    exclusive
                    onChange={(e, newMode) => newMode && setViewMode(newMode)}
                    size="small"
                    sx={{
                      "& .MuiToggleButton-root": {
                        textTransform: "none",
                        fontSize: "0.75rem",
                        px: 1.5,
                        py: 0.5,
                      },
                    }}
                  >
                    <ToggleButton value="edit">
                      <EditIcon sx={{ fontSize: 16, mr: 0.5 }} />
                      Edit
                    </ToggleButton>
                    <ToggleButton value="preview">
                      <VisibilityIcon sx={{ fontSize: 16, mr: 0.5 }} />
                      Preview
                    </ToggleButton>
                  </ToggleButtonGroup>
                  <Tooltip title="Upload PDF">
                    <Box>
                      <input
                        accept="application/pdf"
                        style={{ display: "none" }}
                        id="pdf-upload-button"
                        type="file"
                        onChange={handlePdfUpload}
                        disabled={uploadingPdf}
                      />
                      <label htmlFor="pdf-upload-button">
                        <IconButton
                          component="span"
                          disabled={uploadingPdf}
                          size="small"
                          sx={{
                            backgroundColor: "#e3f2fd",
                            color: "#1565c0",
                            "&:hover": {
                              backgroundColor: "#bbdefb",
                            },
                            "&.Mui-disabled": {
                              backgroundColor: "#f5f5f5",
                              color: "#9e9e9e",
                            },
                          }}
                        >
                          {uploadingPdf ? (
                            <CircularProgress size={20} />
                          ) : (
                            <UploadFileIcon fontSize="small" />
                          )}
                        </IconButton>
                      </label>
                    </Box>
                  </Tooltip>
                </Box>
              </Box>

              {viewMode === "edit" ? (
                <TextField
                  fullWidth
                  multiline
                  rows={6}
                  value={formData.job_description}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      job_description: e.target.value,
                    })
                  }
                  placeholder="Enter the job description here or upload a PDF..."
                  helperText={
                    uploadedFileName
                      ? `Uploaded: ${uploadedFileName}`
                      : "Provide a detailed job description to help with candidate matching (supports Markdown)"
                  }
                  required
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      "&:hover": {
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#1565c0",
                        },
                      },
                    },
                  }}
                />
              ) : (
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    minHeight: 200,
                    maxHeight: 400,
                    overflowY: "auto",
                    borderRadius: 2,
                    backgroundColor: "#fafafa",
                    "& h1": {
                      fontSize: "1.5rem",
                      fontWeight: 700,
                      color: "#1565c0",
                      mt: 2,
                      mb: 1,
                      borderBottom: "2px solid #e3f2fd",
                      pb: 0.5,
                    },
                    "& h2": {
                      fontSize: "1.2rem",
                      fontWeight: 600,
                      color: "#1565c0",
                      mt: 2,
                      mb: 1,
                    },
                    "& h3": {
                      fontSize: "1rem",
                      fontWeight: 600,
                      mt: 1.5,
                      mb: 0.5,
                    },
                    "& p": {
                      mb: 1,
                      lineHeight: 1.6,
                    },
                    "& ul, & ol": {
                      pl: 3,
                      mb: 1,
                    },
                    "& li": {
                      mb: 0.5,
                    },
                    "& strong": {
                      fontWeight: 600,
                      color: "#1565c0",
                    },
                    "& em": {
                      fontStyle: "italic",
                      color: "#666",
                    },
                    "& code": {
                      backgroundColor: "#e3f2fd",
                      padding: "2px 6px",
                      borderRadius: 1,
                      fontFamily: "monospace",
                      fontSize: "0.9em",
                    },
                    "& pre": {
                      backgroundColor: "#263238",
                      color: "#fff",
                      p: 2,
                      borderRadius: 1,
                      overflow: "auto",
                      mb: 2,
                    },
                    "& table": {
                      width: "100%",
                      borderCollapse: "collapse",
                      mb: 2,
                      border: "1px solid #e0e0e0",
                    },
                    "& th": {
                      backgroundColor: "#1565c0",
                      color: "#fff",
                      fontWeight: 600,
                      padding: "8px 12px",
                      textAlign: "left",
                      border: "1px solid #1565c0",
                    },
                    "& td": {
                      padding: "8px 12px",
                      border: "1px solid #e0e0e0",
                    },
                    "& tr:nth-of-type(even)": {
                      backgroundColor: "#f5f5f5",
                    },
                    "& blockquote": {
                      borderLeft: "4px solid #1565c0",
                      pl: 2,
                      ml: 0,
                      fontStyle: "italic",
                      color: "#666",
                    },
                    "& hr": {
                      border: "none",
                      borderTop: "1px solid #e0e0e0",
                      my: 2,
                    },
                  }}
                >
                  {formData.job_description ? (
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {formData.job_description}
                    </ReactMarkdown>
                  ) : (
                    <Typography color="text.secondary" sx={{ fontStyle: "italic" }}>
                      No job description provided. Upload a PDF or type in edit mode.
                    </Typography>
                  )}
                </Paper>
              )}

              {pdfError && (
                <Alert severity="error" sx={{ mt: 1, borderRadius: 2 }}>
                  {pdfError}
                </Alert>
              )}
            </Box>
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              type="number"
              label="Target Candidates"
              value={formData.max_candidates}
              onChange={(e) => {
                const value = parseInt(e.target.value);
                if (value > 0 && value <= 100) {
                  setFormData({
                    ...formData,
                    max_candidates: value,
                  });
                }
              }}
              inputProps={{ min: 1, max: 100 }}
              error={
                formData.max_candidates <= 0 || formData.max_candidates > 100
              }
              helperText={
                formData.max_candidates <= 0
                  ? "Please enter a number greater than 0"
                  : formData.max_candidates > 100
                  ? "Maximum 100 candidates allowed"
                  : ""
              }
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  "&:hover": {
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#1565c0",
                    },
                  },
                },
                "& .MuiInputLabel-root": {
                  fontWeight: 500,
                },
              }}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              type="number"
              label="Expected Salary"
              value={formData.expected_salary || 50000}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  expected_salary: parseInt(e.target.value) || 50000,
                })
              }
              inputProps={{ min: 0 }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  "&:hover": {
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#1565c0",
                    },
                  },
                },
                "& .MuiInputLabel-root": {
                  fontWeight: 500,
                },
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Calendly Link"
              value={formData.calendly_link || ""}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  calendly_link: e.target.value,
                })
              }
              placeholder="https://calendly.com/your-link"
              required
              helperText="Enter your Calendly scheduling link (required)"
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  "&:hover": {
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#1565c0",
                    },
                  },
                },
                "& .MuiInputLabel-root": {
                  fontWeight: 500,
                },
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <Box>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 600, mb: 2, color: "#424242" }}
              >
                Search Locations *
              </Typography>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<LocationIcon />}
                onClick={() => setLocationSelectorOpen(true)}
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 500,
                  py: 1.5,
                  borderWidth: 2,
                  "&:hover": {
                    borderWidth: 2,
                    backgroundColor: "#e3f2fd",
                  },
                }}
              >
                {(() => {
                  const numericIds =
                    formData.search_location?.filter((id) =>
                      /^\d+$/.test(id)
                    ) || [];
                  return numericIds.length > 0
                    ? `${numericIds.length} location${
                        numericIds.length !== 1 ? "s" : ""
                      } selected`
                    : "Select locations";
                })()}
              </Button>
              {formData.search_location &&
                formData.search_location.length > 0 && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      Selected IDs:{" "}
                      {formData.search_location
                        .filter((id) => /^\d+$/.test(id)) // Only show numeric IDs
                        .join(", ")}
                    </Typography>
                  </Box>
                )}
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 600, mb: 2, color: "#424242" }}
            >
              Scale
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Slider
                value={formData.scale}
                min={1}
                max={5}
                step={1}
                onChange={(e, value) =>
                  setFormData({ ...formData, scale: value })
                }
                sx={{
                  flex: 1,
                  color: "#3B82F6",
                  "& .MuiSlider-thumb": {
                    width: 16,
                    height: 16,
                    backgroundColor: "#3B82F6",
                    "&:hover": {
                      boxShadow: "0 0 0 8px rgba(59, 130, 246, 0.16)",
                    },
                  },
                  "& .MuiSlider-track": {
                    height: 6,
                    borderRadius: 3,
                    backgroundColor: "#3B82F6",
                  },
                  "& .MuiSlider-rail": {
                    height: 6,
                    borderRadius: 3,
                    backgroundColor: "#E5E7EB",
                  },
                }}
              />
              <Box
                sx={{
                  minWidth: "48px",
                  height: "36px",
                  backgroundColor: "#3B82F6",
                  borderRadius: "6px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#FFFFFF",
                  fontWeight: 500,
                  fontSize: "14px",
                }}
              >
                {formData.scale}
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Grid>

      {/* Right Column */}
      <Grid item xs={12} md={6}>
        {/* Extracted Data Display */}
        {showExtractedData && extractedData && (
          <Card
            sx={{
              backgroundColor: "#f8f9fa",
              border: "2px solid #e3f2fd",
              borderRadius: 3,
              boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
              display: "block",
            }}
          >
            <CardContent sx={{ textAlign: "center", py: 3 }}>
              <Typography
                variant="h6"
                sx={{ fontWeight: 600, mb: 3, color: "#1565c0" }}
              >
                AI-Generated Configuration
              </Typography>

              <Box sx={{ mb: 3 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 2,
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 600, mb: 2, color: "#424242" }}
                  >
                    Boolean Search String: *
                  </Typography>
                  <Tooltip title="Generate with AI">
                    <IconButton
                      onClick={() => setAiPromptModalOpen(true)}
                      sx={{
                        backgroundColor: "#e3f2fd",
                        color: "#1565c0",
                        "&:hover": {
                          backgroundColor: "#bbdefb",
                        },
                      }}
                    >
                      <AutoAwesomeIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  required
                  value={modifiedBooleanString}
                  onChange={(e) => setModifiedBooleanString(e.target.value)}
                  helperText="Boolean search string is required for candidate sourcing"
                  error={
                    !modifiedBooleanString || !modifiedBooleanString.trim()
                  }
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "white",
                      borderRadius: 2,
                      "&:hover": {
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#1565c0",
                        },
                      },
                    },
                  }}
                />
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 600, mb: 2, color: "#424242" }}
                >
                  Core Skills:
                </Typography>
                <CourseSkillsChips
                  value={formData.core_skills || []}
                  onChange={(skills) =>
                    setFormData({
                      ...formData,
                      core_skills: skills,
                    })
                  }
                  label=""
                  placeholder="Add a skill and press Enter..."
                />
              </Box>

              <Box>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 600, mb: 2, color: "#424242" }}
                >
                  Message Template:
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={5}
                  value={modifiedMessageTemplate}
                  onChange={(e) => setModifiedMessageTemplate(e.target.value)}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "white",
                      borderRadius: 2,
                      "&:hover": {
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#1565c0",
                        },
                      },
                    },
                  }}
                />
              </Box>
            </CardContent>
          </Card>
        )}

        {!showExtractedData && (
          <Card
            sx={{
              width: "411px",
              height: "234px",
              backgroundColor: "#F9FAFB",
              borderRadius: "10px",
              border: "1px solid #E5E7EB",
              boxShadow: "none",
              display: "block",
            }}
          >
            <CardContent sx={{ textAlign: "center", py: 4 }}>
              {/* Sparkle Icon */}
              <Box sx={{ mb: 2 }}>
                <svg
                  width="40"
                  height="40"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M12 2L13.09 8.26L19 9L13.09 9.74L12 16L10.91 9.74L5 9L10.91 8.26L12 2Z"
                    stroke="#9CA3AF"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M19 15L19.54 17.46L22 18L19.54 18.54L19 21L18.46 18.54L16 18L18.46 17.46L19 15Z"
                    stroke="#9CA3AF"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Click "Extract Keywords" to generate AI-powered search strings
                and message templates
              </Typography>
              <Button
                onClick={handleExtractKeywords}
                variant="contained"
                disabled={extractingKeywords || !formData.job_description?.trim()}
                startIcon={
                  extractingKeywords ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : (
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M12 2L13.09 8.26L19 9L13.09 9.74L12 16L10.91 9.74L5 9L10.91 8.26L12 2Z"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M19 15L19.54 17.46L22 18L19.54 18.54L19 21L18.46 18.54L16 18L18.46 17.46L19 15Z"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )
                }
                sx={{
                  backgroundColor: "#155DFC",
                  borderRadius: "24px",
                  textTransform: "none",
                  fontWeight: 500,
                  px: 3,
                  py: 1,
                  "&:hover": {
                    backgroundColor: "#1048C7",
                  },
                }}
              >
                {extractingKeywords ? "Extracting..." : "Extract Keywords"}
              </Button>
            </CardContent>
          </Card>
        )}
      </Grid>

      {/* Location Selector Dialog */}
      <LocationSelector
        open={locationSelectorOpen}
        onClose={() => setLocationSelectorOpen(false)}
        selectedLocations={formData.search_location || []}
        onLocationChange={(locations) =>
          setFormData({
            ...formData,
            search_location: locations,
          })
        }
      />
    </Grid>
  );
};

export default CreateBatchForm;
